import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import { RESULTS, LINKS } from "../mock";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TONE = {
  positive: { dot: "bg-[#4fd18b]", value: "text-[#4fd18b]" },
  negative: { dot: "bg-[#f26d6d]", value: "text-[#f26d6d]" },
};

// Render a note, turning the word "Myfxbook" into a link like the original.
const renderNote = (text) => {
  const parts = text.split("Myfxbook");
  if (parts.length === 1) return text;
  return parts.map((part, i) => (
    <React.Fragment key={i}>
      {part}
      {i < parts.length - 1 && (
        <a
          href={LINKS.myfxbook}
          target="_blank"
          rel="noreferrer"
          className="text-[#e8b876] underline decoration-[#e8b876]/50 underline-offset-2 hover:decoration-[#e8b876]"
        >
          Myfxbook
        </a>
      )}
    </React.Fragment>
  ));
};

const Results = () => {
  const [cards, setCards] = useState(RESULTS.cards);
  const [notes, setNotes] = useState(RESULTS.notes);
  const [live, setLive] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/results`, { timeout: 20000 })
      .then((res) => {
        if (!mounted || !res.data?.cards) return;
        setCards(res.data.cards);
        if (res.data.notes) setNotes(res.data.notes);
        setLive(res.data.source === "myfxbook" && !res.data.stale);
      })
      .catch(() => {
        /* keep mock fallback values */
      });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section id="results" className="relative py-24 sm:py-28 border-t border-white/[0.05]">
      <div className="absolute inset-x-0 top-0 h-[300px] amber-glow opacity-60 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-6">
        {/* Header */}
        <Reveal className="text-center max-w-2xl mx-auto">
          <div className="flex items-center justify-center gap-2.5">
            <span className="section-label">{RESULTS.label}</span>
            {live && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#5eae7a]/30 bg-[#5eae7a]/[0.1] px-2.5 py-0.5 text-[0.62rem] font-semibold text-[#7bd39a] tracking-wide">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[#5eae7a] opacity-75 animate-ping" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#5eae7a]" />
                </span>
                LIVE
              </span>
            )}
          </div>
          <h2 className="font-display font-800 text-white text-[2rem] sm:text-[2.9rem] leading-[1.05] mt-4 tracking-tight">
            {RESULTS.title}
          </h2>
          <p className="text-[#a49e92] text-[1rem] leading-relaxed mt-5">{RESULTS.subtitle}</p>
        </Reveal>

        {/* Amplified stats — single-line toggle */}
        <Reveal delay={120} className="mt-14">
          {(() => {
            const idx = Math.min(active, cards.length - 1);
            const card = cards[idx] || cards[0];
            const tone = TONE[card.tone] || TONE.positive;
            return (
              <div className="rounded-[24px] border border-white/[0.08] bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-6 sm:p-8">
                {/* Toggle tabs */}
                <div className="flex flex-wrap gap-2">
                  {cards.map((c, i) => {
                    const short = c.label.split(" (")[0];
                    const isActive = i === idx;
                    return (
                      <button
                        key={i}
                        onClick={() => setActive(i)}
                        className={`rounded-full px-4 py-2 text-[0.8rem] font-500 transition-all duration-200 ${
                          isActive
                            ? "bg-white/[0.08] text-white border border-white/20"
                            : "text-[#8a8478] border border-white/[0.06] hover:text-white hover:border-white/15"
                        }`}
                      >
                        {short}
                      </button>
                    );
                  })}
                </div>

                {/* Active stat */}
                <div key={idx} className="mt-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 animate-[fadeIn_0.4s_ease]">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2.5">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${tone.dot}`} />
                      <span className="text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-[#8f897d]">
                        {card.label}
                      </span>
                      {card.live && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-[#5eae7a]/30 bg-[#5eae7a]/[0.1] px-2 py-0.5 text-[0.58rem] font-semibold text-[#7bd39a] tracking-wide">
                          <span className="w-1 h-1 rounded-full bg-[#5eae7a]" /> LIVE
                        </span>
                      )}
                    </div>
                    <div className={`font-display font-800 text-[3.6rem] sm:text-[5rem] leading-none mt-3 ${tone.value}`}>
                      {card.value}
                    </div>
                  </div>
                  <p className="text-[#8a8478] text-[0.92rem] leading-relaxed sm:text-right max-w-xs sm:pb-3">
                    {card.sub}
                  </p>
                </div>

                {/* Dots */}
                <div className="flex items-center gap-2 mt-8">
                  {cards.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActive(i)}
                      aria-label={`Show stat ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === idx ? "w-7 bg-[#d69b4e]" : "w-1.5 bg-white/20 hover:bg-white/40"
                      }`}
                    />
                  ))}
                </div>
              </div>
            );
          })()}
        </Reveal>

        {/* Risk notes */}
        <Reveal delay={160} className="mt-10 space-y-5 max-w-5xl">
          {notes.map((note, i) => (
            <p key={i} className="text-[#8a8478] text-[0.92rem] leading-relaxed">
              {renderNote(note)}
            </p>
          ))}
        </Reveal>

        {/* CTA + disclaimer */}
        <Reveal delay={220} className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <p className="text-[#6e685c] text-[0.8rem] leading-relaxed max-w-lg">{RESULTS.disclaimer}</p>
          <a
            href={RESULTS.cta.href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.03] text-white font-600 px-5 py-3 text-[0.9rem] hover:bg-white/[0.08] hover:border-white/25 transition-all duration-200 whitespace-nowrap self-start"
          >
            {RESULTS.cta.label} <ArrowUpRight className="w-4 h-4" />
          </a>
        </Reveal>
      </div>
    </section>
  );
};

export default Results;
